/**
 * Shared sentiment analyzer with expanded civic vocabulary,
 * intensity weighting, and negation handling.
 *
 * Returns "positive" | "neutral" | "negative".
 * Zero API cost — pure keyword matching, no external calls.
 *
 * Used by: apps/web (per-message analysis), apps/api (session aggregation).
 */

// Intensity-weighted keyword lists
// Strong keywords = ±2, mild keywords = ±1
const STRONG_POS = [
  "excellent", "amazing", "outstanding", "love", "brilliant", "fantastic",
  "remarkable", "exceptional", "inspiring", "transformative",
];
const MILD_POS = [
  "great", "good", "support", "agree", "hope", "thank", "wonderful",
  "strong", "proud", "fair", "trust", "honest", "help", "improve",
  "better", "yes", "absolutely", "progress", "reform", "invest",
  "develop", "accountable", "transparent", "empower", "inclusive",
  "deliver", "commitment", "vision", "growth", "opportunity",
  "justice", "equity", "stability", "prosperity",
  "commend", "applaud", "promise", "optimistic",
];
const STRONG_NEG = [
  "terrible", "disgusting", "corrupt", "hate", "atrocious", "despicable",
  "catastrophic", "scandalous", "outrageous", "abysmal",
];
const MILD_NEG = [
  "wrong", "disagree", "liar", "bad", "stupid", "never", "fail",
  "broken", "unfair", "dangerous", "no", "waste", "ruin", "awful",
  "dishonest", "incompetent", "inequality", "poverty", "crisis",
  "scandal", "exploit", "neglect", "abandon", "suppress", "oppress",
  "mismanage", "bankrupt", "insecurity", "inflation", "unemployment",
  "tribalism", "nepotism", "stagnation", "injustice", "authoritarian",
  "disappointed", "frustrated", "worried", "concerned",
];

// Negation tokens flip the next keyword's sentiment within 3 words
const NEGATION = new Set([
  "not", "no", "don't", "doesn't", "didn't", "isn't", "aren't",
  "wasn't", "weren't", "won't", "can't", "cannot", "couldn't",
  "shouldn't", "wouldn't", "hardly", "never", "neither", "nor",
]);

// Build regex patterns once for performance
function buildPatterns(words) {
  return words.map(w => new RegExp(`\\b${w}\\b`, "i"));
}

const strongPosPatterns = buildPatterns(STRONG_POS);
const mildPosPatterns   = buildPatterns(MILD_POS);
const strongNegPatterns = buildPatterns(STRONG_NEG);
const mildNegPatterns   = buildPatterns(MILD_NEG);

/**
 * Check if a keyword at a given position is negated.
 * Scans up to 3 words before the keyword for negation tokens.
 */
function isNegated(words, keywordIndex) {
  const lookback = Math.max(0, keywordIndex - 3);
  for (let i = lookback; i < keywordIndex; i++) {
    if (NEGATION.has(words[i].toLowerCase().replace(/[^a-z']/g, ""))) {
      return true;
    }
  }
  return false;
}

export function analyzeSentiment(text) {
  if (!text || typeof text !== "string") return "neutral";

  const lower = text.toLowerCase();
  const words = lower.split(/\s+/);
  let score = 0;

  // Score each keyword category, applying negation when detected
  function scorePatterns(patterns, baseWeight) {
    for (const pattern of patterns) {
      const match = pattern.exec(lower);
      if (match) {
        // Find the word index of this match
        const matchWord = match[0].toLowerCase();
        const wordIndex = words.findIndex((w, i) =>
          w.replace(/[^a-z']/g, "") === matchWord
            && lower.indexOf(match[0]) >= lower.split(/\s+/).slice(0, i).join(" ").length
        );
        const negated = wordIndex > 0 && isNegated(words, wordIndex);
        score += negated ? -baseWeight : baseWeight;
      }
    }
  }

  scorePatterns(strongPosPatterns, 2);
  scorePatterns(mildPosPatterns, 1);
  scorePatterns(strongNegPatterns, -2);
  scorePatterns(mildNegPatterns, -1);

  return score > 0 ? "positive" : score < 0 ? "negative" : "neutral";
}
